import { useState } from "react";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";

export type SortDirection = "asc" | "desc" | null;

export interface SortConfig<T> {
  key: T;
  direction: SortDirection;
}

interface SortableTableHeaderProps<T> {
  label: string;
  sortKey: T;
  currentSort: SortConfig<T>;
  onSort: (key: T) => void;
}

export function SortableTableHeader<T>({
  label,
  sortKey,
  currentSort,
  onSort,
}: SortableTableHeaderProps<T>) {
  const renderSortIcon = () => {
    if (currentSort.key !== sortKey) {
      return <ArrowUpDown className="h-4 w-4 ml-1 text-gray-400" />;
    }

    if (currentSort.direction === "asc") {
      return <ArrowUp className="h-4 w-4 ml-1 text-gray-800" />;
    }

    if (currentSort.direction === "desc") {
      return <ArrowDown className="h-4 w-4 ml-1 text-gray-800" />;
    }

    return <ArrowUpDown className="h-4 w-4 ml-1 text-gray-400" />;
  };

  return (
    <button
      className="flex items-center hover:text-gray-700"
      onClick={() => onSort(sortKey)}
    >
      {label} {renderSortIcon()}
    </button>
  );
}

export function useTableSorting<T>(
  initialKey: T,
  initialDirection: SortDirection = "asc"
) {
  const [sortConfig, setSortConfig] = useState<SortConfig<T>>({
    key: initialKey,
    direction: initialDirection,
  });

  const handleSort = (key: T) => {
    let direction: SortDirection = "asc";

    if (sortConfig.key === key) {
      if (sortConfig.direction === "asc") {
        direction = "desc";
      } else if (sortConfig.direction === "desc") {
        direction = null;
      }
    }

    setSortConfig({ key, direction });
  };

  return {
    sortConfig,
    handleSort,
  };
}
